/**
 * Flags (#316): a `flags` attribute on paragraphs and headings, drawn as icons
 * in a left margin, toggled with Alt+1..9, the right-click menu, the margin
 * button or the toolbar. The attribute says which flags a line carries
 * ([{ id, type, personId? }]); each flag's state (open or addressed, its
 * reply) comes from the server through `getState`, so addressing a flag never
 * edits the document. See docs/superpowers/specs/2026-10-27-flags-design.md.
 */
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet, type EditorView } from '@tiptap/pm/view';
import { Fragment, Slice, type Node as PmNode } from '@tiptap/pm/model';
import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { LucideIcon } from 'lucide-react';
import type { FlagState, FlagTypeDef } from '../../lib/flags';

export interface FlagEntry { id: string; type: string; personId?: string }

export interface FlagExtensionOptions {
  /** False for templates, annotations and embeds: the attribute still parses, nothing renders, no keys bind. */
  enabled: boolean;
  getState: (id: string) => FlagState | undefined;
  getType: (id: string) => FlagTypeDef;
  /** The types Alt+1..9 reach, in order. */
  getShortcutTypes: () => FlagTypeDef[];
  personInitials: (personId: string) => string;
  /** The person's full name for the tooltip; empty while unknown. */
  personName?: (personId: string) => string;
  onOpenMenu: (a: { pos: number; x: number; y: number; mode: 'kinds' | 'people'; pendingType?: string }) => void;
  onOpenFlag: (a: { flagId: string; pos: number; rect: DOMRect }) => void;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    flags: {
      /** Add the type to the line at `at` (default: the selection), or take it off if it is there. */
      toggleFlag: (type: string, personId?: string, at?: number) => ReturnType;
      clearFlags: (at?: number) => ReturnType;
      setFlagType: (flagId: string, type: string, personId?: string) => ReturnType;
      removeFlag: (flagId: string) => ReturnType;
      revealFlag: (flagId: string, open?: boolean) => ReturnType;
      refreshFlagDecorations: () => ReturnType;
    };
  }
}

const FLAGGABLE = new Set(['paragraph', 'heading']);
export const flagPluginKey = new PluginKey<DecorationSet>('flags');
const newId = () => crypto.randomUUID();
const flagsOf = (node: PmNode): FlagEntry[] => (Array.isArray(node.attrs.flags) ? node.attrs.flags as FlagEntry[] : []);

/** The paragraph or heading containing `pos`, innermost first. */
export function textblockAt(state: EditorState, pos: number): { pos: number; node: PmNode } | null {
  const $pos = state.doc.resolve(Math.max(0, Math.min(pos, state.doc.content.size)));
  for (let d = $pos.depth; d > 0; d--) {
    const node = $pos.node(d);
    if (FLAGGABLE.has(node.type.name)) return { pos: $pos.before(d), node };
  }
  const after = $pos.nodeAfter;
  return after && FLAGGABLE.has(after.type.name) ? { pos: $pos.pos, node: after } : null;
}

export function findFlag(doc: PmNode, flagId: string): { pos: number; node: PmNode } | null {
  let found: { pos: number; node: PmNode } | null = null;
  doc.descendants((node, pos) => {
    if (found) return false;
    if (FLAGGABLE.has(node.type.name) && flagsOf(node).some((f) => f.id === flagId)) { found = { pos, node }; return false; }
    return true;
  });
  return found;
}

/** Every flag in a pasted slice gets a fresh id: a copy is a new, open flag. */
export function reidentify(slice: Slice): Slice {
  const map = (fragment: Fragment): Fragment => {
    const nodes: PmNode[] = [];
    fragment.forEach((node) => {
      if (node.isText) { nodes.push(node); return; }
      const flags = flagsOf(node);
      nodes.push(flags.length
        ? node.type.create({ ...node.attrs, flags: flags.map((f) => ({ ...f, id: newId() })) }, map(node.content), node.marks)
        : node.copy(map(node.content)));
    });
    return Fragment.fromArray(nodes);
  };
  return new Slice(map(slice.content), slice.openStart, slice.openEnd);
}

const setFlags = (tr: Transaction, pos: number, node: PmNode, flags: FlagEntry[]) =>
  tr.setNodeMarkup(pos, undefined, { ...node.attrs, flags });

// A stable small number per icon component, for widget keys.
const iconIds = new Map<LucideIcon, number>();
const iconKey = (icon: LucideIcon) => {
  if (!iconIds.has(icon)) iconIds.set(icon, iconIds.size + 1);
  return iconIds.get(icon)!;
};

/**
 * Pasted content gets fresh flag ids (a copy is a new, open flag) - except a
 * line moved by drag inside the editor, which keeps its id and so its state.
 */
export function pastedTransform(slice: Slice, view: EditorView): Slice {
  return (view as unknown as { dragging?: { move?: boolean } | null }).dragging?.move ? slice : reidentify(slice);
}

// Icon markup, rendered once per icon through React (lucide icons are components).
const iconCache = new Map<LucideIcon, string>();
// Static markup, not a mounted root: decorations are often built while React is
// rendering, where flushSync does nothing and the icon came out blank.
function iconSvg(icon: LucideIcon): string {
  let html = iconCache.get(icon);
  if (html === undefined) {
    html = renderToStaticMarkup(createElement(icon, { size: 16, 'aria-hidden': true }));
    if (html) iconCache.set(icon, html);
  }
  return html;
}

function gutter(view: EditorView, node: PmNode, opts: FlagExtensionOptions): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'flag-gutter';
  wrap.contentEditable = 'false';
  const flags = flagsOf(node);
  if (flags.length === 0) {
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'flag-btn flag-add';
    add.title = 'Add a flag';
    add.setAttribute('aria-label', 'Add a flag');
    add.innerHTML = iconSvg(opts.getType('').icon);
    // Open on click, not on the press (#329): a menu opened during mousedown is
    // closed again by its own click-outside listener catching that same press.
    add.addEventListener('mousedown', (e) => e.preventDefault());
    add.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = add.getBoundingClientRect();
      const pos = view.posAtDOM(wrap, 0);
      const block = textblockAt(view.state, pos);
      if (block) opts.onOpenMenu({ pos: block.pos, x: rect.left, y: rect.bottom + 4, mode: 'kinds' });
    });
    wrap.appendChild(add);
    return wrap;
  }
  for (const f of flags) {
    const def = opts.getType(f.type);
    const st = opts.getState(f.id);
    const done = st?.status === 'addressed';
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `flag-btn${done ? ' flag-done' : ''}`;
    b.dataset.flagId = f.id;
    b.style.setProperty('--fc', def.color);
    const who = f.personId ? opts.personName?.(f.personId) ?? '' : '';
    const label = `${def.label}${who ? ` ${who}` : ''}${done ? ' - addressed' : def.addressable ? ' - open' : ''}`;
    b.title = label;
    b.setAttribute('aria-label', label);
    b.innerHTML = iconSvg(def.icon);
    if (f.personId) {
      const ini = document.createElement('span');
      ini.className = 'flag-initials';
      ini.textContent = opts.personInitials(f.personId);
      b.appendChild(ini);
    }
    if (done) {
      const ok = document.createElement('span');
      ok.className = 'flag-check';
      b.appendChild(ok);
    }
    b.addEventListener('mousedown', (e) => e.preventDefault());
    b.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const at = findFlag(view.state.doc, f.id);
      if (at) opts.onOpenFlag({ flagId: f.id, pos: at.pos, rect: b.getBoundingClientRect() });
    });
    wrap.appendChild(b);
  }
  return wrap;
}

function replies(node: PmNode, opts: FlagExtensionOptions): HTMLElement | null {
  const lines = flagsOf(node).flatMap((f) => {
    const st = opts.getState(f.id);
    const def = opts.getType(f.type);
    return st?.status === 'addressed' && st.reply ? [`${def.replyLabel ?? 'Note'}: ${st.reply}`] : [];
  });
  if (lines.length === 0) return null;
  const wrap = document.createElement('span');
  wrap.className = 'flag-reply';
  wrap.contentEditable = 'false';
  wrap.textContent = lines.join('\n');
  return wrap;
}

function buildDecorations(state: EditorState, opts: FlagExtensionOptions): DecorationSet {
  const decos: Decoration[] = [];
  state.doc.descendants((node, pos) => {
    if (!FLAGGABLE.has(node.type.name)) return true;
    const flags = flagsOf(node);
    // The key changes with everything the widget shows - state, and the type's
    // name, colour and icon and the person's initials, which load after the
    // document - so ProseMirror redraws it then and reuses it otherwise.
    const sig = flags.map((f) => {
      const st = opts.getState(f.id);
      const def = opts.getType(f.type);
      const who = f.personId ? `${opts.personInitials(f.personId)}|${opts.personName?.(f.personId) ?? ''}` : '';
      return `${f.id}:${f.type}:${f.personId ?? ''}:${who}:${st?.status ?? '-'}:${def.label}:${def.color}:${def.addressable}:${iconKey(def.icon)}`;
    }).join('|');
    decos.push(Decoration.widget(pos + 1, (view) => gutter(view, node, opts), { side: -1, key: `g${sig}`, ignoreSelection: true, stopEvent: () => true }));
    if (flags.length) {
      const addressable = flags.filter((f) => opts.getType(f.type).addressable);
      if (addressable.length && addressable.every((f) => opts.getState(f.id)?.status === 'addressed')) {
        decos.push(Decoration.node(pos, pos + node.nodeSize, { class: 'flag-alldone' }));
      }
      const replySig = flags.map((f) => { const st = opts.getState(f.id); return st?.status === 'addressed' ? `${f.id}:${st.reply ?? ''}` : ''; }).join('|');
      if (replySig.replace(/\|/g, '')) {
        decos.push(Decoration.widget(pos + node.nodeSize - 1, () => replies(node, opts) ?? document.createElement('span'),
          { side: 1, key: `r${replySig}`, ignoreSelection: true, stopEvent: () => true }));
      }
    }
    return false;
  });
  return DecorationSet.create(state.doc, decos);
}

/**
 * A flag id on two lines. When the lines are the two halves of a split line
 * (their text joins back into the old line's), the flag stays with the half
 * that has text - the first one if both do - and leaves the other: Enter
 * neither duplicates a flag nor strands an answered question on an empty
 * line. Any other duplicate (a copy) gets a fresh id.
 */
function fixDuplicates(old: EditorState, state: EditorState): Transaction | null {
  const where = new Map<string, Array<{ pos: number; node: PmNode }>>();
  state.doc.descendants((node, pos) => {
    if (!FLAGGABLE.has(node.type.name)) return true;
    for (const f of flagsOf(node)) where.set(f.id, [...(where.get(f.id) ?? []), { pos, node }]);
    return false;
  });
  const dups = [...where].filter(([, at]) => at.length > 1);
  if (dups.length === 0) return null;
  const tr = state.tr;
  for (const [id, at] of dups) {
    const was = findFlag(old.doc, id)?.node.textContent;
    const split = was !== undefined && at.map((a) => a.node.textContent).join('') === was;
    const keep = split ? (at.find((a) => a.node.textContent.length > 0) ?? at[0]) : at[0];
    for (const a of at) {
      if (a === keep) continue;
      const node = tr.doc.nodeAt(a.pos)!;
      const flags = flagsOf(node).flatMap((f) => (f.id !== id ? [f] : split ? [] : [{ ...f, id: newId() }]));
      tr.setNodeMarkup(a.pos, undefined, { ...node.attrs, flags });
    }
  }
  return tr;
}

function coords(view: EditorView, pos: number): { x: number; y: number } {
  try {
    const c = view.coordsAtPos(pos + 1);
    return { x: c.left, y: c.bottom + 4 };
  } catch {
    return { x: 0, y: 0 };
  }
}

export const FlagExtension = Extension.create<FlagExtensionOptions>({
  name: 'flags',

  addOptions() {
    return {
      enabled: false,
      getState: () => undefined,
      getType: (id: string) => ({ id, label: 'Flag', plural: 'flags', icon: (null as unknown as LucideIcon), color: 'currentColor', addressable: true, replyLabel: 'Note', custom: true, archived: false }),
      getShortcutTypes: () => [],
      personInitials: () => '',
      onOpenMenu: () => {},
      onOpenFlag: () => {},
    };
  },

  addGlobalAttributes() {
    return [{
      types: ['paragraph', 'heading'],
      attributes: {
        flags: {
          default: [],
          keepOnSplit: false,
          parseHTML: (el) => {
            try {
              const v = JSON.parse(el.getAttribute('data-flags') ?? '[]');
              return Array.isArray(v) ? v : [];
            } catch { return []; }
          },
          renderHTML: (attrs) => (Array.isArray(attrs.flags) && attrs.flags.length ? { 'data-flags': JSON.stringify(attrs.flags) } : {}),
        },
      },
    }];
  },

  addCommands() {
    const blockFor = (state: EditorState, at?: number) => textblockAt(state, at ?? state.selection.head);
    return {
      toggleFlag: (type, personId, at) => ({ state, tr, dispatch }) => {
        const block = blockFor(state, at);
        if (!block) return false;
        const flags = flagsOf(block.node);
        const byPerson = personId !== undefined;
        const match = (f: FlagEntry) => f.type === type && (!byPerson || f.personId === personId);
        const next = flags.some(match)
          ? flags.filter((f) => !match(f))
          : [...flags, personId ? { id: newId(), type, personId } : { id: newId(), type }];
        if (dispatch) setFlags(tr, block.pos, block.node, next);
        return true;
      },
      clearFlags: (at) => ({ state, tr, dispatch }) => {
        const block = blockFor(state, at);
        if (!block || flagsOf(block.node).length === 0) return false;
        if (dispatch) setFlags(tr, block.pos, block.node, []);
        return true;
      },
      setFlagType: (flagId, type, personId) => ({ state, tr, dispatch }) => {
        const at = findFlag(state.doc, flagId);
        if (!at) return false;
        if (dispatch) {
          setFlags(tr, at.pos, at.node, flagsOf(at.node).map((f) => (f.id === flagId
            ? (personId ? { id: f.id, type, personId } : { id: f.id, type }) : f)));
        }
        return true;
      },
      removeFlag: (flagId) => ({ state, tr, dispatch }) => {
        const at = findFlag(state.doc, flagId);
        if (!at) return false;
        if (dispatch) setFlags(tr, at.pos, at.node, flagsOf(at.node).filter((f) => f.id !== flagId));
        return true;
      },
      revealFlag: (flagId, open) => ({ editor }) => {
        const at = findFlag(editor.state.doc, flagId);
        if (!at) return false;
        const dom = editor.view.nodeDOM(at.pos);
        if (dom instanceof HTMLElement) {
          dom.scrollIntoView?.({ block: 'center' });
          dom.classList.remove('flag-flash');
          void dom.offsetWidth;
          dom.classList.add('flag-flash');
          setTimeout(() => dom.classList.remove('flag-flash'), 1400);
          if (open) {
            const btn = dom.querySelector(`[data-flag-id="${CSS.escape(flagId)}"]`);
            this.options.onOpenFlag({ flagId, pos: at.pos, rect: (btn ?? dom).getBoundingClientRect() });
          }
        }
        return true;
      },
      refreshFlagDecorations: () => ({ tr, dispatch }) => {
        if (dispatch) tr.setMeta(flagPluginKey, 'refresh');
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    const opts = this.options;
    // Even where flags are off (excerpt embeds, templates), a pasted flagged
    // line must not bring another document's ids into this one.
    if (!opts.enabled) return [new Plugin({ key: new PluginKey('flagsPaste'), props: { transformPasted: pastedTransform } })];
    return [new Plugin<DecorationSet>({
      key: flagPluginKey,
      state: {
        init: (_config, state) => buildDecorations(state, opts),
        apply: (tr, old, _prev, next) => (tr.docChanged || tr.getMeta(flagPluginKey) ? buildDecorations(next, opts) : old),
      },
      props: {
        attributes: { class: 'flags-enabled' },
        decorations: (state) => flagPluginKey.getState(state),
        transformPasted: pastedTransform,
        handleKeyDown: (v, event) => {
          if (!v.editable || !event.altKey || event.ctrlKey || event.metaKey) return false;
          const m = /^Digit(\d)$/.exec(event.code);
          if (!m) return false;
          const block = textblockAt(v.state, v.state.selection.head);
          if (!block) return false;
          const n = Number(m[1]);
          if (n === 0) {
            event.preventDefault();
            const flags = flagsOf(block.node);
            if (flags.length) v.dispatch(setFlags(v.state.tr, block.pos, block.node, []));
            return true;
          }
          const type = opts.getShortcutTypes()[n - 1];
          if (!type) return false;
          event.preventDefault();
          if (type.id === 'discuss') {
            opts.onOpenMenu({ pos: block.pos, ...coords(v, block.pos), mode: 'people', pendingType: 'discuss' });
            return true;
          }
          const flags = flagsOf(block.node);
          const next = flags.some((f) => f.type === type.id)
            ? flags.filter((f) => f.type !== type.id)
            : [...flags, { id: newId(), type: type.id }];
          v.dispatch(setFlags(v.state.tr, block.pos, block.node, next));
          return true;
        },
        handleDOMEvents: {
          contextmenu: (v, event) => {
            if (!v.editable || event.shiftKey) return false;
            const hit = v.posAtCoords({ left: event.clientX, top: event.clientY });
            if (!hit) return false;
            const block = textblockAt(v.state, hit.pos);
            if (!block) return false;
            event.preventDefault();
            opts.onOpenMenu({ pos: block.pos, x: event.clientX, y: event.clientY, mode: 'kinds' });
            return true;
          },
        },
      },
      appendTransaction: (trs, old, state) => (trs.some((t) => t.docChanged) ? fixDuplicates(old, state) : null),
    })];
  },
});
